import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const PlanningConges = () => {
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [periodeDebut, setPeriodeDebut] = useState('');
  const [periodeFin, setPeriodeFin] = useState('');

  const fetchConges = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (filter !== 'tous') {
        params.statut = filter;
      }
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }
      if (periodeDebut) {
        params.periode_debut = periodeDebut;
      }
      if (periodeFin) {
        params.periode_fin = periodeFin;
      }
      const response = await http.get('/api/conges', { params });
      setConges(response.data.data || response.data || []);
      setTotal(response.data.total || response.data.length || 0);
    } catch (error) {
      console.error('Erreur récupération congés:', error);
      setConges([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [filter, filialeId, isConsolidated, periodeDebut, periodeFin]);

  useEffect(() => {
    fetchConges();
  }, [page, filter, filialeId, isConsolidated, periodeDebut, periodeFin]);

  const handleValider = async (id) => {
    try {
      await http.put(`/api/conges/${id}`, { statut: 'valide' });
      fetchConges();
      alert('Demande validée avec succès');
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleRejeter = async (id) => {
    const motif = prompt('Motif du rejet :');
    if (motif) {
      try {
        await http.put(`/api/conges/${id}`, { statut: 'rejete', motif_rejet: motif });
        fetchConges();
        alert('Demande rejetée');
      } catch (error) {
        console.error('Erreur rejet:', error);
        alert('Erreur lors du rejet');
      }
    }
  };

  const getEtatBadge = (etat) => {
    switch (etat) {
      case 0: return { text: 'En attente', className: 'bg-yellow-100 text-yellow-600' };
      case 1: return { text: 'Validé', className: 'bg-green-100 text-green-600' };
      case 2: return { text: 'Prévalidé', className: 'bg-blue-100 text-blue-600' };
      case 3: return { text: 'Rejeté', className: 'bg-red-100 text-red-600' };
      case 4: return { text: 'Annulé', className: 'bg-gray-100 text-gray-600' };
      default: return { text: 'Inconnu', className: 'bg-gray-100 text-gray-600' };
    }
  };

  const getStatutBadge = (conge) => {
    if (conge.etat === 1) {
      const dateDebut = new Date(conge.date_debut);
      const aujourdHui = new Date();
      aujourdHui.setHours(0, 0, 0, 0);
      dateDebut.setHours(0, 0, 0, 0);

      if (dateDebut < aujourdHui) {
        return { text: 'Parti', className: 'bg-green-100 text-green-600' };
      } else if (dateDebut > aujourdHui) {
        return { text: 'A venir', className: 'bg-red-100 text-red-600' };
      } else {
        return { text: "Aujourd'hui", className: 'bg-blue-100 text-blue-600' };
      }
    }
    return null;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Planning des Congés"
        subtitle="Visualiser et gérer les demandes de congé"
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchConges,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="space-y-4">
          {/* Statut filters */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-3.5 w-3.5 text-sky-700" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Statut
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('tous')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'tous' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setFilter('prevalide')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'prevalide' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Pré-validés
              </button>
              <button
                onClick={() => setFilter('valide')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'valide' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Validés
              </button>
              <button
                onClick={() => setFilter('rejete')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'rejete' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Rejetés
              </button>
            </div>
          </div>

          {/* Période filters */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-3.5 w-3.5 text-sky-700" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Période
              </span>
            </div>
            <div className="flex gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Date début
                </label>
                <input
                  type="date"
                  value={periodeDebut}
                  onChange={(e) => setPeriodeDebut(e.target.value)}
                  className="w-40 h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Date fin
                </label>
                <input
                  type="date"
                  value={periodeFin}
                  onChange={(e) => setPeriodeFin(e.target.value)}
                  className="w-40 h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 transition-colors"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { setPeriodeDebut(''); setPeriodeFin(''); }}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-sky-500 hover:text-sky-700 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            Chargement…
          </div>
        ) : conges.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-slate-500">
            Aucune demande de congé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-sky-50/60">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Employé</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Type de congé</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date début</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date fin</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date reprise</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Jours</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">État</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {conges.map((conge) => (
                  <tr key={conge.idplanning_conge} className="hover:bg-sky-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">
                      {conge.prenom} {conge.nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {conge.type_conge_libelle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(conge.date_debut).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(conge.date_fin).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {conge.date_reprise ? new Date(conge.date_reprise).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-600">
                        {conge.nombre_jour}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getEtatBadge(conge.etat).className}`}>
                        {getEtatBadge(conge.etat).text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatutBadge(conge) && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatutBadge(conge).className}`}>
                          {getStatutBadge(conge).text}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {conge.etat === 0 && (
                          <button type="button" onClick={() => handleValider(conge.idplanning_conge)} className="text-green-600 hover:text-green-800">
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        {conge.etat === 0 && (
                          <button type="button" onClick={() => handleRejeter(conge.idplanning_conge)} className="text-red-600 hover:text-red-800">
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <div className="text-sm text-slate-500">
              Page {page} sur {totalPages} ({total} éléments)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border border-slate-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border border-slate-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default PlanningConges;
